import React, { useState, useEffect } from "react";
import api from "../config/api";
import "./AddEnterpriseModal.css";
import { toast } from "react-toastify";
import Select from "react-select";
import { formSelectStyles } from "../utils/selectStyles";
import { LANGUAGE_OPTIONS } from "../utils/language";

interface AddEnterpriseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (enterprise: any) => void;
}

const AddEnterpriseModal: React.FC<AddEnterpriseModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    nom: "",
    sirene: "",
    adresse: "",
    mail: "",
    country: "",
    // French by default: the enterprises on file are French.
    language: "fr",
    sendContracts: true,
  });
  const [loading, setLoading] = useState(false);
  const [coders, setCoders] = useState<any[]>([]);
  const [loadingCoders, setLoadingCoders] = useState(false);
  const [selectedCoderIds, setSelectedCoderIds] = useState<number[]>([]);
  useEffect(() => {
    if (isOpen) {
      fetchCoders();
    }
  }, [isOpen]);

  const fetchCoders = async () => {
    try {
      setLoadingCoders(true);
      // Fetch all coders with a high limit to avoid pagination issues in the select
      const response = await api.get("/api/coders?limit=10000");
      // Only coders without an enterprise. Test the `enterprise` relation, not an
      // `enterpriseId` field: the Coder entity declares no such property, so it is
      // absent from the payload and the check would match every coder.
      const availableCoders = response.data.data.filter(
        (coder: any) => !coder.enterprise,
      );
      setCoders(availableCoders);
    } catch (error) {
      console.error("Error loading coders:", error);
      toast.error("Erreur lors du chargement des codeurs");
    } finally {
      setLoadingCoders(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create enterprise
      const enterpriseResponse = await api.post("/api/enterprises", {
        nom: formData.nom,
        sirene: formData.sirene || null,
        adresse: formData.adresse || null,
        mail: formData.mail || null,
        country: formData.country,
        language: formData.language,
        sendContracts: formData.sendContracts,
      });

      const enterprise = enterpriseResponse.data;

      // Link all selected coders to the enterprise
      if (selectedCoderIds.length > 0) {
        const linkPromises = selectedCoderIds.map((coderId) =>
          api.put(`/api/coders/${coderId}`, {
            enterpriseId: enterprise.id ? parseInt(enterprise.id) : null,
          }),
        );

        try {
          await Promise.all(linkPromises);
          toast.success(
            `Entreprise créée avec ${selectedCoderIds.length} codeur(s) lié(s) !`,
          );
        } catch (error: any) {
          console.error("Error linking coders to enterprise:", error);
          toast.warning(
            "Entreprise créée mais erreur lors de la liaison de certains codeurs",
          );
        }
      } else {
        toast.success("Entreprise créée avec succès !");
      }

      onSave(enterprise);
      onClose();
      // Reset form
      setFormData({
        nom: "",
        sirene: "",
        adresse: "",
        mail: "",
        country: "",
        language: "fr",
        sendContracts: true,
      });
      setSelectedCoderIds([]);
      fetchCoders();
    } catch (error: any) {
      console.error("Error creating enterprise:", error);

      let errorMessage = "Erreur lors de la création de l'entreprise";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 500) {
        errorMessage = "Erreur serveur. Vérifiez vos données.";
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Ajouter une Entreprise</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="nom">Nom de l'Entreprise *</label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              required
              placeholder="Nom de l'entreprise"
            />
          </div>

          <div className="form-group">
            <label htmlFor="sirene">SIREN</label>
            <input
              type="text"
              id="sirene"
              name="sirene"
              value={formData.sirene}
              onChange={handleChange}
              placeholder="Numéro SIREN"
            />
          </div>

          <div className="form-group">
            <label htmlFor="adresse">Adresse</label>
            <textarea
              id="adresse"
              name="adresse"
              value={formData.adresse}
              onChange={handleChange}
              placeholder="Adresse de l'entreprise"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="mail">Email</label>
            <input
              type="email"
              id="mail"
              name="mail"
              value={formData.mail}
              onChange={handleChange}
              placeholder="Email de l'entreprise"
            />
          </div>

          <div className="form-group">
            <label htmlFor="country">Pays *</label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              placeholder="Pays"
            />
          </div>

          <div className="form-group">
            <label htmlFor="language">Langue *</label>
            <select
              id="language"
              name="language"
              value={formData.language}
              onChange={handleChange}
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group checkbox-group">
            <label
              htmlFor="sendContracts"
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                id="sendContracts"
                name="sendContracts"
                checked={!formData.sendContracts}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    sendContracts: !e.target.checked,
                  }))
                }
                style={{ marginRight: "8px" }}
              />
              Ne pas envoyer de contrats
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="coderIds">Lier des Codeurs (optionnel)</label>
            {/* The select stays mounted while the coders load: swapping it for a
                line of text made the dialog shorter, then taller as it arrived.
                `isLoading` shows react-select's own indicator in place. */}
            <Select
              name="coderIds"
              isMulti
              options={coders.map((coder) => ({
                value: coder.id,
                label: `${coder.first_name} ${coder.last_name} (${coder.email})`,
              }))}
              value={selectedCoderIds
                .map((id) => {
                  const coder = coders.find((c) => c.id === id);
                  return coder
                    ? {
                        value: coder.id,
                        label: `${coder.first_name} ${coder.last_name} (${coder.email})`,
                      }
                    : null;
                })
                .filter(Boolean)}
              onChange={(selectedOptions) =>
                setSelectedCoderIds(
                  selectedOptions
                    ? selectedOptions.map((opt: any) => opt.value)
                    : [],
                )
              }
              placeholder="Sélectionner des codeurs..."
              styles={formSelectStyles}
              isLoading={loadingCoders}
              isSearchable
              maxMenuHeight={200}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <p
              style={{
                color: "rgba(255, 255, 255, 0.6)",
                fontSize: "0.85rem",
                marginTop: "8px",
                marginBottom: 0,
              }}
            >
              Les codeurs sélectionnés recevront leurs contrats à l'adresse email
              de l'entreprise
            </p>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? "Création..." : "Créer l'Entreprise"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEnterpriseModal;
